import React, { useState } from "react"
import webify from "../util/webify"
import DatePicker from "./DatePicker"
import { formatISO, parseISO } from "date-fns"

interface PackagePrice {
  id?: string
  temp_id?: string
  name: string
  valid_from: string
  price: string
  in_use?: boolean
}

interface PackagePricingProps {
  packagePrices: PackagePrice[]
  errors?: any
}

const PackagePricing = (props: PackagePricingProps) => {
  const initialPrices = props.packagePrices && props.packagePrices.length > 0
    ? props.packagePrices
    : [{
        name: "Base Price",
        valid_from: formatISO(new Date(), { representation: "date" }),
        price: "",
        temp_id: "new_" + Date.now().toString(),
        in_use: false
      }]

  const [packagePrices, setPackagePrices] = useState<PackagePrice[]>(initialPrices)

  const addPackagePrice = () => {
    setPackagePrices([...packagePrices, {
      name: "",
      valid_from: "",
      price: "",
      temp_id: "new_" + Date.now().toString(),
      in_use: false
    }])
  }

  const removePackagePrice = (index: number) => {
    if (packagePrices.length > 1) {
      const newPrices = [...packagePrices]
      newPrices.splice(index, 1)
      setPackagePrices(newPrices)
    }
  }

  const updatePackagePrice = (index: number, field: keyof PackagePrice, value: string) => {
    const newPrices = [...packagePrices]
    newPrices[index] = { ...newPrices[index], [field]: value }
    setPackagePrices(newPrices)
  }

  return (
    <div>
      <label className="label-styled">Package Prices</label>
      <p className="mt-1 mb-3 text-sm text-gray-600">
        Add one price for fixed pricing, or multiple prices with different dates for date-based pricing.
      </p>

      {props.errors && (
        <span className="text-red-600 text-sm">
          {props.errors}
        </span>
      )}

      {packagePrices
        .sort((a, b) => {
          if (!a.valid_from) return 1
          if (!b.valid_from) return -1
          return parseISO(a.valid_from).getTime() - parseISO(b.valid_from).getTime()
        })
        .map((price, index) => (
        <div key={price.temp_id || price.id || `index_${index}`} className="p-4 mb-4 border rounded-lg bg-gray-50">
          <div className="form-grid-setup sm:grid-cols-6">
            <div className="sm:col-span-2">
              <label className="label-styled">Name</label>
              <input
                type="text"
                name={`package[package_prices][${index}][name]`}
                value={price.name}
                onChange={(e) => updatePackagePrice(index, 'name', e.target.value)}
                placeholder="Early Bird Price"
                className="form-control"
                maxLength={60}
              />
            </div>
            <div className="sm:col-span-2">
              <label className="label-styled">Valid From</label>
              <DatePicker
                name={`package[package_prices][${index}][valid_from]`}
                value={price.valid_from}
                fromYear={2018}
                toYear={2050}
                data-role="package-price-valid-from"
                onChange={(date) => updatePackagePrice(index, 'valid_from', formatISO(date, { representation: "date" }))}
              />
            </div>
            <div>
              <label className="label-styled">Price</label>
              <input
                type="text"
                name={`package[package_prices][${index}][price]`}
                value={price.price}
                onChange={(e) => updatePackagePrice(index, 'price', e.target.value)}
                placeholder="0.00"
                className="form-control"
              />
            </div>
            <div className="flex items-end">
              {!price.in_use && packagePrices.length > 1 && (
                <button
                  type="button"
                  onClick={() => removePackagePrice(index)}
                  className="delete-button"
                >
                  Remove
                </button>
              )}
            </div>
          </div>
          {price.id && (
            <input
              type="hidden"
              name={`package[package_prices][${index}][id]`}
              value={price.id}
            />
          )}
        </div>
      ))}

      <div className="mb-4">
        <button type="button" onClick={addPackagePrice} className="mt-2 button secondary">
          Add Package Price
        </button>
      </div>
    </div>
  )
}

export default PackagePricing
webify("package-pricing", PackagePricing)